// @ts-check

import { useCallback } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import {
  getOrgConfigForTenant as getOrgConfigForTenantQuery,
  updateOrgConfigForTenant as updateOrgConfigForTenantMutation,
} from "src/queries/orgConfig.graphql";
import { omitTypename } from "src/util/graphql";

/**
 * Hook for managing tenant AI customisations config.
 * Unlike the base useOrgConfigForTenant hook, this throws on mutation errors
 * so callers can display guardrail validation messages to the user.
 */
export const useAiCustomisationsForTenant = () => {
  const { data, loading } = useQuery(getOrgConfigForTenantQuery, {
    fetchPolicy: "cache-first",
  });

  const [updateConfig, { loading: updating }] = useMutation(
    updateOrgConfigForTenantMutation
  );

  const config = useMemo(() => omitTypename(data?.config), [data]);

  const updateAiCustomisations = useCallback(
    /**
     * @param {org_config.tenant.AiCustomisations} newAiCustomisations
     * @returns {Promise<void>}
     * @throws {Error} if validation fails (e.g. guardrail rejection)
     */
    async (newAiCustomisations) => {
      const { errors } = await updateConfig({
        variables: {
          input: { config: { aiCustomisations: newAiCustomisations } },
        },
        update: (cache, { data: updateData, errors: updateErrors }) => {
          if (updateErrors?.length || !updateData?.config) {
            return;
          }
          const existingData = cache.readQuery({
            query: getOrgConfigForTenantQuery,
          });
          if (existingData) {
            cache.writeQuery({
              query: getOrgConfigForTenantQuery,
              data: { config: updateData.config },
            });
          }
        },
      });

      if (errors?.length) {
        const error = new Error(errors[0]?.message);
        // @ts-expect-error - attaching graphQLErrors for error handling
        error.graphQLErrors = errors;
        throw error;
      }
    },
    [updateConfig]
  );

  return {
    loading,
    aiCustomisations: config?.config?.aiCustomisations,
    update: updateAiCustomisations,
    updating,
  };
};
